import { useCallback, useRef, useState } from 'react';

/**
 * useScoreAnnouncer — Web Speech API (TTS) hook for live-match score calls.
 *
 * announce(scoringScore, otherScore, isCorrection)
 *   → "Score [scoringScore] [otherScore]"          (normal point)
 *   → "Score correction [scoringScore] [otherScore]" (-1 correction)
 *
 * The scoring team's score is always spoken first.
 * When muted the utterance is stored but not spoken (repeat still works).
 *
 * @param {boolean} initialMuted - starting mute state (e.g. viewers default
 *   to muted so a live match doesn't start talking unexpectedly; editors
 *   default to unmuted).
 */
const useScoreAnnouncer = (initialMuted = false) => {
  const [muted, setMuted] = useState(initialMuted);
  const lastAnnouncementRef = useRef('');

  const speak = useCallback((text) => {
    if (!window.speechSynthesis) return;
    // Cancel any currently playing utterance so the new one plays immediately
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  }, []);

  /**
   * @param {number} scoringScore  - the score of the team that just changed
   * @param {number} otherScore    - the other team's score
   * @param {boolean} isCorrection - true when this is a -1 / score-decrease call
   */
  const announce = useCallback((scoringScore, otherScore, isCorrection = false) => {
    const text = isCorrection
      ? `Score correction. ${scoringScore}. ${otherScore}.`
      : `Score. ${scoringScore}. ${otherScore}.`;

    lastAnnouncementRef.current = text;

    if (!muted) {
      speak(text);
    }
  }, [muted, speak]);

  const repeat = useCallback(() => {
    if (!lastAnnouncementRef.current) return;
    if (!muted) {
      speak(lastAnnouncementRef.current);
    }
  }, [muted, speak]);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      if (!prev) {
        // Muting — cancel any in-progress speech
        if (window.speechSynthesis) window.speechSynthesis.cancel();
      }
      return !prev;
    });
  }, []);

  return { muted, announce, repeat, toggleMute };
};

export default useScoreAnnouncer;
